import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertCircle, Search, RefreshCw, Download, ChevronDown, ChevronUp, List, Layers } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface TaskLog {
  id: number;
  task_id: string;
  user_id: string;
  step: string;
  message: string | null;
  data: any;
  created_at: string;
  tasks?: { product_name: string } | null;
  profiles?: { full_name: string | null } | null;
}

export function ErrorLogsTable() {
  const { toast } = useToast();
  const { session } = useAuth();
  const [logs, setLogs] = useState<TaskLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [stepFilter, setStepFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<"chronological" | "grouped">("chronological");
  const pageSize = 20;

  useEffect(() => {
    loadLogs();
  }, [page, stepFilter]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-actions", {
        body: {
          action: "get_all_logs",
          limit: pageSize,
          offset: (page - 1) * pageSize,
          step: stepFilter
        },
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });

      if (error) throw error;

      setLogs(data?.logs || []);
      setTotalCount(data?.total || 0);
    } catch (error) {
      console.error("Error loading logs:", error);
      toast({
        title: "Erro ao carregar logs",
        description: "Não foi possível carregar os logs do sistema.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      log.message?.toLowerCase().includes(search) ||
      log.step.toLowerCase().includes(search) ||
      log.task_id.toLowerCase().includes(search)
    );
  });

  const toggleRow = (logId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const toggleTask = (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedTasks(newExpanded);
  };

  const groupedLogs = filteredLogs.reduce((acc, log) => {
    if (!acc[log.task_id]) {
      acc[log.task_id] = [];
    }
    acc[log.task_id].push(log);
    return acc;
  }, {} as Record<string, TaskLog[]>);

  const getStepBadgeColor = (step: string) => {
    if (step.includes("error") || step.includes("fail")) {
      return "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400";
    }
    if (step.includes("admin")) {
      return "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400";
    }
    if (step === "persona" || step === "scripts") {
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400";
    }
    if (step === "flux" || step === "sora") {
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400";
    }
    return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400";
  };

  const exportLogs = () => {
    const csv = [
      ["ID", "Task ID", "Step", "Message", "User", "Created At"].join(","),
      ...filteredLogs.map((log) =>
        [
          log.id,
          log.task_id,
          log.step,
          `"${log.message?.replace(/"/g, '""') || ""}"`,
          log.profiles?.full_name || "Unknown",
          new Date(log.created_at).toLocaleString("pt-BR"),
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              Logs do Sistema
            </CardTitle>
            <CardDescription>
              {totalCount} logs registrados · Página {page} de {totalPages}
              {viewMode === "grouped" && ` · ${Object.keys(groupedLogs).length} tasks`}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={viewMode === "chronological" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("chronological")}
            >
              <List className="w-4 h-4 mr-2" />
              Cronológico
            </Button>
            <Button
              variant={viewMode === "grouped" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grouped")}
            >
              <Layers className="w-4 h-4 mr-2" />
              Por Task
            </Button>
            <Button variant="outline" size="sm" onClick={exportLogs}>
              <Download className="w-4 h-4 mr-2" />
              Exportar
            </Button>
            <Button variant="outline" size="sm" onClick={loadLogs}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Buscar logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={stepFilter} onValueChange={setStepFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Filtrar por step" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os steps</SelectItem>
              <SelectItem value="error">Erros</SelectItem>
              <SelectItem value="persona">Persona</SelectItem>
              <SelectItem value="scripts">Scripts</SelectItem>
              <SelectItem value="flux">Flux</SelectItem>
              <SelectItem value="sora">Sora</SelectItem>
              <SelectItem value="admin_cancel">Admin Cancel</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="border rounded-lg">
          <div className="max-h-[600px] overflow-y-auto">
            {viewMode === "chronological" ? (
              <table className="w-full">
                <thead className="bg-muted sticky top-0">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium w-8"></th>
                    <th className="text-left p-3 text-sm font-medium">Data/Hora</th>
                    <th className="text-left p-3 text-sm font-medium">Step</th>
                    <th className="text-left p-3 text-sm font-medium">Task</th>
                    <th className="text-left p-3 text-sm font-medium">Mensagem</th>
                    <th className="text-left p-3 text-sm font-medium">Usuário</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-muted-foreground">
                        Carregando logs...
                      </td>
                    </tr>
                  ) : filteredLogs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8 text-muted-foreground">
                        Nenhum log encontrado
                      </td>
                    </tr>
                  ) : (
                    filteredLogs.map((log) => {
                      const isExpanded = expandedRows.has(log.id);
                      const hasLongMessage = (log.message?.length || 0) > 100;
                      return (
                        <>
                          <tr key={log.id} className="border-t hover:bg-muted/50">
                            <td className="p-3">
                              {hasLongMessage && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0"
                                  onClick={() => toggleRow(log.id)}
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </td>
                            <td className="p-3 text-sm whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {new Date(log.created_at).toLocaleDateString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "2-digit",
                                  })}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {new Date(log.created_at).toLocaleTimeString("pt-BR", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                  })}
                                </span>
                              </div>
                            </td>
                            <td className="p-3">
                              <span className={`text-xs px-2 py-1 rounded font-medium ${getStepBadgeColor(log.step)}`}>
                                {log.step}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              <div className="flex flex-col">
                                <span className="font-medium truncate max-w-[200px]">
                                  {log.tasks?.product_name || "N/A"}
                                </span>
                                <span className="text-xs text-muted-foreground font-mono">
                                  {log.task_id.slice(0, 8)}...
                                </span>
                              </div>
                            </td>
                            <td className="p-3 text-sm max-w-md">
                              <div className={isExpanded ? "" : "truncate"}>
                                {log.message || "-"}
                              </div>
                            </td>
                            <td className="p-3 text-sm text-muted-foreground">
                              {log.profiles?.full_name || "Unknown"}
                            </td>
                          </tr>
                          {isExpanded && log.data && (
                            <tr key={`${log.id}-expanded`} className="border-t bg-muted/30">
                              <td colSpan={6} className="p-4">
                                <div className="space-y-2">
                                  <p className="text-sm font-medium">Dados adicionais:</p>
                                  <pre className="text-xs bg-background p-3 rounded border overflow-x-auto">
                                    {JSON.stringify(log.data, null, 2)}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })
                  )}
                </tbody>
              </table>
            ) : (
              <div className="space-y-2 p-2">
                {loading ? (
                  <div className="text-center p-8 text-muted-foreground">
                    Carregando logs...
                  </div>
                ) : Object.keys(groupedLogs).length === 0 ? (
                  <div className="text-center p-8 text-muted-foreground">
                    Nenhum log encontrado
                  </div>
                ) : (
                  Object.entries(groupedLogs).map(([taskId, taskLogs]) => {
                    const isTaskExpanded = expandedTasks.has(taskId);
                    const firstLog = taskLogs[0];
                    const hasErrors = taskLogs.some(log => log.step.includes("error") || log.step.includes("fail"));

                    return (
                      <div key={taskId} className="border rounded-lg">
                        <div
                          className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50"
                          onClick={() => toggleTask(taskId)}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                            >
                              {isTaskExpanded ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : (
                                <ChevronDown className="h-4 w-4" />
                              )}
                            </Button>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium">{firstLog.tasks?.product_name || "Task sem nome"}</p>
                                {hasErrors && (
                                  <span className="text-xs px-2 py-1 rounded bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                                    Com erros
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground font-mono">{taskId.slice(0, 16)}...</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">{taskLogs.length} logs</span>
                            <span className="text-muted-foreground">{firstLog.profiles?.full_name || "Unknown"}</span>
                          </div>
                        </div>

                        {isTaskExpanded && (
                          <div className="border-t">
                            <table className="w-full">
                              <thead className="bg-muted/50">
                                <tr>
                                  <th className="text-left p-2 text-xs font-medium">Data/Hora</th>
                                  <th className="text-left p-2 text-xs font-medium">Step</th>
                                  <th className="text-left p-2 text-xs font-medium">Mensagem</th>
                                </tr>
                              </thead>
                              <tbody>
                                {taskLogs.map((log) => {
                                  const isExpanded = expandedRows.has(log.id);
                                  const hasLongMessage = (log.message?.length || 0) > 100;
                                  return (
                                    <>
                                      <tr key={log.id} className="border-t hover:bg-muted/30">
                                        <td className="p-2 text-xs whitespace-nowrap">
                                          {new Date(log.created_at).toLocaleString("pt-BR", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                          })}
                                        </td>
                                        <td className="p-2">
                                          <span className={`text-xs px-2 py-1 rounded font-medium ${getStepBadgeColor(log.step)}`}>
                                            {log.step}
                                          </span>
                                        </td>
                                        <td className="p-2 text-xs">
                                          <div className="flex items-center gap-2">
                                            {hasLongMessage && (
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-5 w-5 p-0"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleRow(log.id);
                                                }}
                                              >
                                                {isExpanded ? (
                                                  <ChevronUp className="h-3 w-3" />
                                                ) : (
                                                  <ChevronDown className="h-3 w-3" />
                                                )}
                                              </Button>
                                            )}
                                            <div className={isExpanded ? "" : "truncate"}>
                                              {log.message || "-"}
                                            </div>
                                          </div>
                                        </td>
                                      </tr>
                                      {isExpanded && log.data && (
                                        <tr key={`${log.id}-expanded`} className="bg-muted/20">
                                          <td colSpan={3} className="p-3">
                                            <div className="space-y-1">
                                              <p className="text-xs font-medium">Dados adicionais:</p>
                                              <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
                                                {JSON.stringify(log.data, null, 2)}
                                              </pre>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Anterior
            </Button>
            <span className="text-sm text-muted-foreground">
              Página {page} de {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Próxima
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
